<template>
    <div>
        <h2 class="font-header text-[3rem] font-bold text-primary mb-6">Contact Information</h2>

        <div class="bg-white rounded-lg shadow-md p-6">
            <div v-if="isLoading" class="text-center py-8">
                <Loader size="medium" />
            </div>

            <div v-else class="space-y-6">
                <div class="flex justify-between items-center mb-4">
                    <h3 class="font-header text-[1.5rem] font-bold">Contact Fields</h3>
                    <button @click="addField"
                        class="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        Add Field
                    </button>
                </div>

                <div v-if="fields.length === 0"
                    class="text-center py-8 text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                    No contact fields configured. Click "Add Field" to add one.
                </div>

                <div v-else class="space-y-4">
                    <div v-for="(field, index) in fields" :key="index"
                        class="border border-gray-300 rounded-lg p-4 space-y-4">
                        <div class="flex justify-between items-start">
                            <h4 class="font-header text-[1.5rem] font-bold text-primary">Field {{ index + 1 }}</h4>
                            <button @click="removeField(index)"
                                class="text-red-600 hover:text-red-800 font-body text-[1.125rem] leading-[1.75]">
                                Remove
                            </button>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label
                                    class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Type</label>
                                <select v-model="field.type"
                                    class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]">
                                    <option value="email">Email</option>
                                    <option value="phone">Phone</option>
                                    <option value="address">Address</option>
                                </select>
                            </div>

                            <div>
                                <label
                                    class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Label</label>
                                <input v-model="field.label" type="text"
                                    class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                    placeholder="e.g., Email" />
                            </div>

                            <div>
                                <label
                                    class="block font-body text-[1.125rem] leading-[1.75] text-primary mb-2">Value</label>
                                <input v-model="field.value" type="text"
                                    class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                    :placeholder="field.type === 'email' ? 'email@example.com' : field.type === 'phone' ? '+1 (234) 567-8900' : 'Address'" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="flex justify-end gap-4 pt-4 border-t border-gray-200">
                    <button @click="() => loadContactInfo()"
                        class="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        Cancel
                    </button>
                    <button @click="saveContactInfo" :disabled="isSaving"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75] disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ isSaving ? 'Saving...' : 'Save Changes' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { adminService } from '../../services/admin'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'

const fields = ref([])
const { loading: isLoading, setLoading } = useLoadingState(false)
const isSaving = ref(false)

const loadContactInfo = async () => {
    setLoading(true)
    try {
        const response = await adminService.getContactInfo()
        fields.value = response.fields || []
    } catch (error) {
        console.error('Failed to load contact info:', error)
        alert('Failed to load contact information. Please try again.')
        fields.value = []
    } finally {
        setLoading(false)
    }
}

const addField = () => {
    fields.value.push({
        type: 'email',
        label: '',
        value: ''
    })
}

const removeField = (index) => {
    if (confirm('Are you sure you want to remove this field?')) {
        fields.value.splice(index, 1)
    }
}

const saveContactInfo = async () => {
    if (fields.value.length === 0) {
        alert('Please add at least one contact field.')
        return
    }

    for (const field of fields.value) {
        if (!field.type || !field.label || !field.value) {
            alert('Please fill in all fields (Type, Label, and Value) for each contact field.')
            return
        }
    }

    isSaving.value = true
    try {
        await adminService.updateContactInfo(fields.value)
        alert('Contact information saved successfully!')
    } catch (error) {
        console.error('Failed to save contact info:', error)
        alert('Failed to save contact information. Please try again.')
    } finally {
        isSaving.value = false
    }
}

onMounted(() => {
    loadContactInfo()
})
</script>

<style scoped></style>
